import { type JSX, useMemo } from 'react';
import { useGate, useUnit } from 'effector-react';
import {
  FormControl,
  FormHelperText,
  FormLabel,
  TextField,
  CircularProgress,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { Listbox } from '@features/effector-form/components/listbox';
import {
  ApartmentControlGate,
  apartmentsQuery,
} from '@features/effector-form/controls/apartment/model';
import type {
  ApartmentControlType,
  ApartmentOption,
} from '@features/effector-form/controls/apartment/types';
import { withControl } from '@features/effector-form/hoc/withControl';
import { getAutocompleteOnChange } from '../../lib/ get-autocomplete-on-change';
import { getAutocompleteValue } from '../../lib/get-autocomplete-value';

export const ApartmentControl = withControl<ApartmentControlType>(
  ({
    value,
    setValue,
    error,
    label,
    required,
    readOnly,
    disabled,
    complex,
    building,
    isError,
    buildingIds = [],
    multiple,
  }) => {
    const memoBuildingIds = useMemo(() => buildingIds, [buildingIds.join(',')]);

    useGate(ApartmentControlGate, {
      complex,
      building,
      buildingIds: memoBuildingIds,
    });

    const { data, pending } = useUnit(apartmentsQuery);

    const options = Array.isArray(data) ? data : [];

    const handleChange = (
      _: unknown,
      newValue: string | ApartmentOption | (string | ApartmentOption)[] | null
    ) => getAutocompleteOnChange(newValue, setValue, multiple);

    return (
      <FormControl required={required} disabled={disabled}>
        {label && <FormLabel error={isError}>{label}</FormLabel>}
        <Autocomplete
          multiple={multiple}
          disableCloseOnSelect={multiple}
          loading={pending}
          value={getAutocompleteValue(options, value, multiple)}
          options={options}
          onChange={handleChange}
          getOptionKey={(option) => option.id}
          slotProps={{
            listbox: {
              component: Listbox,
            },
          }}
          renderInput={(params): JSX.Element => (
            <TextField
              {...params}
              slotProps={{
                input: {
                  ...params.InputProps,
                  error: isError,
                  readOnly,
                  endAdornment: readOnly ? null : (
                    <>
                      {pending && <CircularProgress size={20} color="inherit" />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
          readOnly={readOnly}
          disabled={disabled}
        />
        {error && <FormHelperText error>{error}</FormHelperText>}
      </FormControl>
    );
  }
);
